import { ErrorMsg } from 'commonTypes'
import { CompoundDetailsWithRelatedDataDomain } from 'compoundTypes'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { HeaderCellProps, TableRowData } from 'tableTypes'
import { debounce } from 'throttle-debounce'
import { useDataContext } from 'context/DataContext'
import { Routes } from 'common/routeConfig'
import { filterByCategory, filterByGds } from 'utils/evidenceFilterUtil'
import FilterSearchField from 'components/filters/compound/FilterSearchField'
import { useCompoundFilterContext } from 'context/CompoundFilterContext'
import CompoundFilter from 'components/filters/compound/CompoundFilter'
import FilterChips from 'components/filters/FilterChips'
import { useCompoundsPageStyles } from './Compound.styles'
import DefaultCard from '../../components/card/DefaultCard'
import FilterBar from '../../components/filters/FilterBar'
import ProgressBarWithValueIndicator from '../../components/progressbar/ProgressBarWithValueIndicator'
import TableWithGenomateScoreColumn from '../../components/table/TableWithGenomateScoreColumn'
import ReportMenuCommonLayout from '../../components/reportMenuCommonLayout/ReportMenuCommonLayout'
import { mappedCompoundToTableRowData } from '../../mappers/compoundMappers'
import {
    getMaxGenomateScoreFromLeveledList,
    getMinGenomateScoreFromLeveledList
} from '../../utils/mtcUtils'

type CompoundsProps = {
    isMenuOpen: boolean
}

const Compounds: React.FC<CompoundsProps> = ({ isMenuOpen }) => {
    const { t } = useTranslation('compound')
    const styles = useCompoundsPageStyles()
    const { patientId: patientIdParam, reportId: reportIdParam } = useParams()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [data, setData] = useState<{
        default: Array<CompoundDetailsWithRelatedDataDomain>
        table: Array<TableRowData>
        maxGds: number
        minGds: number
    }>({ default: [], table: [], maxGds: 0, minGds: 0 })

    const [errorMsg] = useState<ErrorMsg>()

    const {
        activeGdsFilter,
        setActiveGdsFilter,
        activeSelectFilters,
        handleActiveSelectFilterChange
    } = useCompoundFilterContext()

    const [inputValue, setInputValue] = useState<string>('')

    const { compounds } = useDataContext()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const listViewBoxRef = useRef<HTMLSpanElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const headerCells: Array<HeaderCellProps> = [
        { id: 'compound', sortable: true, type: 'string' },
        { id: 'drivers', sortable: false },
        { id: 'targets', sortable: false },
        { id: 'gds', sortable: true, type: 'number' }
    ]

    const createTableRows = useCallback(
        (compounds: Array<CompoundDetailsWithRelatedDataDomain>) => {
            return (compounds || []).map((compound) =>
                mappedCompoundToTableRowData(compound, pathname, t)
            )
        },
        [pathname, t]
    )

    const loadCompounds = useCallback(() => {
        const leveledListCompounds = compounds.map(({ level }) => ({
            level
        }))
        const maxGds = getMaxGenomateScoreFromLeveledList(leveledListCompounds)
        const minGds = getMinGenomateScoreFromLeveledList(leveledListCompounds)

        setData({ default: compounds, table: [], maxGds, minGds })
        setIsLoading(false)
    }, [compounds])

    const handleInputChange = useMemo(
        () =>
            debounce(250, (value: string) => {
                setInputValue(value)
            }),
        []
    )

    const filteredCompounds = useMemo(() => {
        if (!data.default?.length) return []

        let result = data.default

        if (inputValue) {
            const lowerInput = inputValue.toLowerCase()
            result = result.filter((compound) =>
                (compound.name ?? '').toLowerCase().includes(lowerInput)
            )
        }

        result = filterByGds(result, activeGdsFilter)
        result = filterByCategory(result, activeSelectFilters)

        return result
    }, [data.default, inputValue, activeGdsFilter, activeSelectFilters])

    const filteredTableRows = useMemo(() => {
        return createTableRows(filteredCompounds)
    }, [createTableRows, filteredCompounds])

    useEffect(() => {
        setData((prev) => ({ ...prev, table: filteredTableRows }))
    }, [filteredTableRows])

    useEffect(() => {
        if (!isLoading && containerRef.current) {
            containerRef.current.scrollIntoView({
                block: 'start',
                behavior: 'smooth'
            })
        }
    }, [isLoading])

    useEffect(() => {
        if (!compounds) {
            navigate(Routes.BASE)
            window.location.reload()
        }
        loadCompounds()
    }, [patientIdParam, reportIdParam, loadCompounds, navigate, compounds])

    return (
        <ReportMenuCommonLayout
            isLoading={isLoading}
            isMenuOpen={isMenuOpen}
            title={t('title')}
            targetUrl="targets"
            error={false}
            errorMsg={errorMsg}
            scrollRef={listViewBoxRef}
            ref={containerRef}
            hasFilters={true}
            subtitle={''}
            tableTitle={''}
        >
            <FilterBar
                filter={<CompoundFilter />}
                chips={
                    <FilterChips
                        chips={[
                            {
                                key: 'compound-category',
                                items: activeSelectFilters,
                                labelKey: (v) => `filter:filter.${v}`,
                                onDelete: handleActiveSelectFilterChange,
                                showDivider: true
                            },
                            {
                                key: 'gds-filter',
                                items: activeGdsFilter ? [activeGdsFilter] : [],
                                labelKey: (v) => `filter:filter.${v}`,
                                onDelete: () => setActiveGdsFilter(''),
                                showDivider: !!activeSelectFilters.length
                            }
                        ]}
                    />
                }
            >
                <FilterSearchField onInputChange={handleInputChange} />
            </FilterBar>
            <DefaultCard>
                {data.table.length > 0 ? (
                    <TableWithGenomateScoreColumn<'compound' | 'gds'>
                        header={{
                            cells: headerCells,
                            translationNamespace: 'compound',
                            translationKey: 'tableHeader'
                        }}
                        tableRows={data.table}
                    >
                        <ProgressBarWithValueIndicator
                            max={data.maxGds}
                            min={data.minGds}
                        />
                    </TableWithGenomateScoreColumn>
                ) : (
                    <span className={styles.emptyState}>
                        <Trans t={t} i18nKey="noCompoundsFound" />
                    </span>
                )}
            </DefaultCard>
        </ReportMenuCommonLayout>
    )
}

export default Compounds